import { Component, OnDestroy, OnInit} from '@angular/core';
import { BaseWithBundleComponent } from '@app/shared/components/base/base-with-bundle.component';
import { ComponentServiceBundle } from '@app/core/services/base/component-service-bundle.service';
import { marker } from '@biesbjerg/ngx-translate-extract-marker';
import { ClientUserUriBuilderParams, UserService } from '@app/core/services/user.service';
import { ClientUser, ClientUserQueryResult, ClientUserState } from '@app/shared/models/client-user';
import {
    ActionListBtnConfig,
    ActionListItem,
    ActionListOption
} from '@app/shared/components/action-list/action-list.component';
import { OP } from '@app/core/services/base/base-service-with-bundle.service';
import { SearchFilter } from '@app/shared/models/search-filter';
import { ContactTypes } from '@app/shared/models/contact';
import { ClientService } from '@app/core/services/client.service';
import { BaseList, BaseWithIdList } from '@app/shared/models/list';
import { UserRole } from '@app/shared/models/user-role';
import { FilterStructure } from '@app/shared/models/filter-structure';
import { UserAssignment } from '@app/shared/models/user-assignment';
import { SmpError } from '@app/shared/models/smp-error';
import { Client } from '@app/shared/models/client';
import { AuthenticationService } from '@app/core/authentication';
import { UserCreateDialogComponent } from '@app/shared/dialogs/user-create-dialog/user-create-dialog.component';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
    templateUrl: './user-management.component.html',
    styleUrls: ['./user-management.component.scss']
})
export class UserManagementComponent extends BaseWithBundleComponent
                                     implements OnInit, OnDestroy {

    public client: Client;

    public clientUserQueryResult: ClientUserQueryResult;
    public clientRoles: BaseWithIdList<UserRole>;
    public userAssignments: BaseList<UserAssignment>;
    public selectedUser: ClientUser;
    public actionListOptions: ActionListBtnConfig<ClientUser>[];
    public filter: SearchFilter<ClientUser>;
    public licenseInfo: string;

    public stateFilters: FilterStructure[];
    public selectedStateFilter: FilterStructure;

    get ClientUserState() {
        return ClientUserState;
    }

    get finalFiltered(): ActionListItem[] {
        const final = this.filter.filteredArray.filter(this.selectedStateFilter.filter) as ClientUser[];

        if (this.selectedUser) {
            const selectedIsInFiltered = final.find(user => user.id === this.selectedUser.id);

            if (!selectedIsInFiltered) {
                this.selectedUser = null;
            }
        }

        return final;
    }

    constructor(protected componentServiceBundle: ComponentServiceBundle,
                protected userService: UserService,
                protected clientService: ClientService,
                protected authService: AuthenticationService) {
        super(componentServiceBundle);
        this._idPrefix = 'user_management.';
    }

    public ngOnInit() {
        super.ngOnInit();
        this.filter = new SearchFilter(false, true);

        this.initActionListOptions();
        this.initStateFilter();
        // Setting ALL as initial state filter (last one in the filter list):
        this.setStateFilter(this.stateFilters[this.stateFilters.length - 1]);

        if (this.currentUser) {
            this.client = this.currentUser.client;

            this.fetchFullUserList();

            this.clientService.getClientRoles(this.client.id)
                              .subscribe(result => {
                    this.clientRoles = result;
                });

            this.clientService.getUserAssignments(this.client.id, true)
                              .subscribe(result => {
                    this.userAssignments = result;
                });
        }
    }

    public ngOnDestroy() {
        super.ngOnDestroy();
    }

    private initActionListOptions(): void {
        this.actionListOptions = [
            // Using the existing options of the action list for COPY and DELETE
            {
                option: ActionListOption.COPY,
                order: 3
            }, {
                option: ActionListOption.DELETE,
                order: 5
            },
            // New options which only exists for the user management
            {
                option: 'INVITE',
                label: marker('user_management.btn.invite'),
                action: (item: ClientUser) => {
                    this.inviteUser(item);
                },
                order: 1
            }, {
                option: 'ACTIVATE',
                label: marker('user_management.btn.activate'),
                action: (item: ClientUser) => {
                    this.toggleActivateUser(item);
                },
                show: (item: ClientUser) => {
                    return item.inactive;
                },
                order: 2
            }, {
                option: 'DEACTIVATE',
                label: marker('user_management.btn.deactivate'),
                action: (item: ClientUser) => {
                    this.toggleActivateUser(item);
                },
                show: (item: ClientUser) => {
                    return !item.inactive;
                },
                order: 2
            }, {
                option: 'PASSWORD_RESET',
                label: marker('user_management.btn.password_reset'),
                action: (item: ClientUser) => {
                    this.resetUserPassword(item);
                },
                order: 4
            }, {
                option: 'DEACTIVATE_2FA',
                label: marker('user_management.btn.deactivate_2fa'),
                action: (item: ClientUser) => {
                    this.deactivateTwoFA(item);
                },
                show: (item: ClientUser) => {
                    return !!item.authentication_method;
                },
                order: 4
            }, {
                option: 'LOG_OFF',
                label: marker('user_management.btn.log_off'),
                action: (item: ClientUser) => {
                    this.logOffUser(item);
                },
                order: 6
            }
        ];
    }

    private initStateFilter(): void {
        this.stateFilters = [
            {
                trans: marker('user_management.state_filter.pending'),
                amount: () => {
                    return this.clientUserQueryResult && this.clientUserQueryResult.count_users_pending;
                },
                filter: (user: ClientUser) => {
                    return user.state === ClientUserState.INVITED;
                }
            }, {
                trans: marker('user_management.state_filter.not_invited'),
                amount: () => {
                    return this.clientUserQueryResult && this.clientUserQueryResult.count_users_not_invited;
                },
                filter: (user: ClientUser) => {
                    return user.state === ClientUserState.NOTINVITED || user.state === ClientUserState.TEMPORARY;
                }
            }, {
                trans: marker('user_management.state_filter.locked'),
                dividerBelow: true,
                amount: () => this.clientUserQueryResult && this.clientUserQueryResult.count_users_locked,
                filter: (user: ClientUser) => user.locked
            }, {
                trans: marker('user_management.state_filter.active'),
                amount: () => {
                    return this.clientUserQueryResult && this.clientUserQueryResult.count_users_active;
                },
                filter: (user: ClientUser) => {
                    return !user.inactive;
                }
            }, {
                trans: marker('user_management.state_filter.inactive'),
                dividerBelow: true,
                amount: () => {
                    return this.clientUserQueryResult && this.clientUserQueryResult.count_users_inactive;
                },
                filter: (user: ClientUser) => {
                    return user.inactive;
                }
            }, {
                trans: marker('user_management.state_filter.mfa_activated'),
                amount: () => {
                    return this.clientUserQueryResult && this.clientUserQueryResult.count_users_mfa_verified;
                },
                filter: (user: ClientUser) => {
                    return user.isVerified;
                }
            }, {
                trans: marker('user_management.state_filter.mfa_deactivated'),
                amount: () => {
                    return this.clientUserQueryResult && this.clientUserQueryResult.count_users_mfa_deactivated;
                },
                filter: (user: ClientUser) => {
                    return !user.authentication_method;
                }
            }, {
                trans: marker('user_management.state_filter.mfa_not_verified'),
                dividerBelow: true,
                amount: () => {
                    return this.clientUserQueryResult&& this.clientUserQueryResult.count_users_mfa_not_verified;
                },
                filter: (user: ClientUser) => {
                    return user.authentication_method && !user.isVerified;
                }
            }, {
                trans: marker('user_management.state_filter.all'),
                amount: () => {
                    return this.clientUserQueryResult && this.clientUserQueryResult.count_users_overall;
                },
                filter: (user: ClientUser) => {
                    return true;
                }
            }
        ];
    }


    // SEARCH METHODS ******************************************************************************************************************

    public setStateFilter(filter: FilterStructure): void {
        this.selectedStateFilter = filter;
    }


    // USER METHODS ***************************************************************d*****************************************************

    protected fetchFullUserList(userIdToSelect?: string): void {
        this.userService.query(this.generateUriBuilderParams())
                        .subscribe((result: ClientUserQueryResult) => {
                this.clientUserQueryResult = result;
                this.licenseInfo = this.translateService.instant(marker('user_management.label.license_info'), {
                    in_use: this.clientUserQueryResult.count_licences_in_use,
                    overall: this.clientUserQueryResult.count_licences_overall
                });
                this.filter.array = this.clientUserQueryResult.users.items;

                if (userIdToSelect) {
                    this.selectUser(this.findUser(userIdToSelect));
                } else if (this.finalFiltered && this.finalFiltered.length > 0) {
                    this.selectUser(this.findUser((<ClientUser>this.finalFiltered[0]).id));
                }
            }, err => this.handleError(err));
    }

    protected updateUser(user: ClientUser, urlSuffix: string, msg?: string): void {
        this.userService.update(this.generateUriBuilderParams(user), user, {urlSuffix: urlSuffix})
                        .subscribe(result => {
            this.fetchFullUserList();

            if (msg) {
                this.showSimpleConfirmSnackbar(msg);
            }
        }, err => this.handleError(err));
    }

    private findUser(userId: string): ClientUser {
        return this.clientUserQueryResult.users.getItem(userId);
    }

    public selectUser(user: ClientUser): void {
        this.selectedUser = null;
        this.userService.getById(this.generateUriBuilderParams(user))
                        .subscribe(result => {
                this.selectedUser = result;
            }, err => this.handleError(err));
    }

    public inviteUser(user: ClientUser): void {
        this.updateUser(user, 'invitation');
    }

    public toggleActivateUser(user: ClientUser): void {
        const data = [{
            op: OP.replace,
            path: 'inactive',
            value: !user.inactive
        }];

        this.userService.patch(this.generateUriBuilderParams(user), data)
                        .subscribe(result => {
                this.fetchFullUserList();
            }, err => this.handleError(err));
    }

    public createUser(user?: ClientUser, msg?: string): void {
        const isCopy = !!user;

        if (!user) {
            user = new ClientUser({
                address: {
                    contacts: [
                        {name: this.translateService.instant(ContactTypes.EMAIL.trans), value: ''},
                        {name: this.translateService.instant(ContactTypes.FAX.trans), value: ''},
                        {name: this.translateService.instant(ContactTypes.PHONE.trans), value: ''}
                    ]
                }
            });

            msg = marker('user_management.message.user_created');
        }

        user.address.splitCountry();

        const dialogRef = this.dialog.open(UserCreateDialogComponent, {
            width: '400px',
            data: {
                title: `${this.translateService.instant(isCopy ? marker('user_management.dialog.copy_user.title') : marker('user_management.dialog.create_user.title'), { name: [user.firstname, user.lastname].join(' ') || user.email } )}`,
                confirmBtnOverride: marker('common.btn.ok'),
                closeBtnOverride: marker('common.btn.cancel'),

                email: user.email,
                userCode: user.user_code
            }
        });

        dialogRef.afterClosed().subscribe((modalResult: { email: string, userCode: string }) => {
            if (modalResult) {
                user.email = modalResult.email;
                user.user_code = modalResult.userCode;

                this.userService.create(this.generateUriBuilderParams(), user)
                        .subscribe(result => {
                    this.fetchFullUserList(result.id);

                    if (msg) {
                        this.showSimpleConfirmSnackbar(msg);
                    }
                }, err => this.handleError(err));
            }
        });
 
    }

    public copyUser(user: ClientUser): void {
        const copied = new ClientUser(user);
        delete copied.id;
        delete copied.email;
        delete copied.email_lower;

        this.createUser(copied, marker('user_management.message.user_copied'));
    }

    public resetUserPassword(user: ClientUser): void {
        this.updateUser(user, 'resetpassword', marker('user_management.message.user_password_reset'));
    }

    public deactivateTwoFA(user: ClientUser): void {
        this.userService.promptForPassword().then((password) => {
            this.authService.verifyCredentials(password).subscribe(result => {
                this.userService.deactivateTwoFA(this.generateUriBuilderParams(user)).subscribe(_ => {
                    user.authentication_method = null;
                    this.selectUser(user);
                });
            }, _ => {
                this.showSimpleInfoDialog(
                    marker('user_management.dialog.invalid_password.title'),
                    marker('user_management.dialog.invalid_password.text'));
            });
        });
    }

    public deleteUser(user: ClientUser): void {
        this.userService.delete(this.generateUriBuilderParams(user))
                        .subscribe(result => {
                this.showSimpleConfirmSnackbar(marker('user_management.message.user_deleted'));
                this.fetchFullUserList();
                if (this.selectedUser && this.selectedUser.id === user.id) {
                    this.selectedUser = null;
                }
            }, err => this.handleError(err));
    }

    public logOffUser(user: ClientUser): void {
        this.authenticationService.logoutClientUser(user)
                                  .subscribe(result => {
                this.showSimpleConfirmSnackbar(marker('user_management.message.user_logged_off'));
            }, err => this.handleError(err));
    }

    public cancelEdit(): void {
        this.selectedUser = null;
    }

    public updateInList(user: ClientUser): void {
        this.showSimpleConfirmSnackbar(marker('user_management.message.user_saved'));
        this.selectedUser = user;
        const limitedUserInList = this.clientUserQueryResult.users.getItem(user.id);
        limitedUserInList.firstname = user.firstname;
        limitedUserInList.lastname = user.lastname;
        this.clientUserQueryResult.users.replaceItem(limitedUserInList);
        this.filter.filter();
    }

    protected handleError(error: Error | string): void {
        error = error instanceof HttpErrorResponse ? (<HttpErrorResponse>error).error : error;

        if ((<SmpError>error).additional_code === 'validation.mandatory.field.missing.email') {
            this.showSimpleErrorSnackbar(marker('user_management.error.email.required'));
        } else if ((<SmpError>error).additional_code === 'client.userlicensecount.limit.reached') {
            this.showSimpleErrorSnackbar(marker('user_management.error.licenses_limit_reached'));
        } else if ((<SmpError>error).additional_code === 'validation.duplicate.email') {
            this.showSimpleErrorSnackbar(marker('user_management.error.email.exists'));
        } else if ((<SmpError>error).additional_code === 'validation.mandatory.field.missing.user_code') {
            this.showSimpleErrorSnackbar(marker('user_management.error.user_code.required'));
        }
            
        super.handleError(error);
    }

    protected generateUriBuilderParams(user?: ClientUser): ClientUserUriBuilderParams {
        return new ClientUserUriBuilderParams(this.client, user);
    }
}
